/**
 * SVGGenerationContext - Shares useSVGGeneration results with child components
 *
 * Calls useSVGGeneration once and exposes the results via context,
 * so any descendant (layer components, zoom/pan, etc.) can consume
 * pre-computed render data without calling the hook independently.
 *
 * Place this provider above any component that needs SVG generation data:
 *
 * ```tsx
 * <MolecularSVGProvider workspace={workspace}>
 *   <SVGGenerationProvider>
 *     <MotionProvider>
 *       <ComposableSVG>
 *         <BondLayer />
 *         <AtomLayer />
 *       </ComposableSVG>
 *     </MotionProvider>
 *   </SVGGenerationProvider>
 * </MolecularSVGProvider>
 * ```
 */

import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';
import { useMolecularSVG } from './MolecularSVGContext';
import { useSVGGeneration } from '../rendering/useSVGGeneration';
import type { SVGGenerationResult } from '../rendering/useSVGGeneration';
import { useLockedViewBox } from '../rendering/lockViewBox';

/**
 * Context value exposing SVG generation results to descendant components
 */
export interface SVGGenerationContextValue extends SVGGenerationResult {
  /** Locked viewBox (initial value, stable during interaction) */
  lockedViewBox: string;
}

const SVGGenerationContext = createContext<SVGGenerationContextValue | null>(null);

export interface SVGGenerationProviderProps {
  children: ReactNode;
}

/**
 * Calls useSVGGeneration and shares results with children via context.
 * Skips recomputation when only positions changed (PIPH-71).
 *
 * Must be inside a MolecularSVGProvider.
 */
export function SVGGenerationProvider({ children }: SVGGenerationProviderProps) {
  const { workspace, svgWidth, svgHeight, alignment, latestChange } = useMolecularSVG();

  // Cache previous generation result to skip recomputation on position-only changes
  const cachedGenerationRef = useRef<SVGGenerationResult | null>(null);

  // Check if we can skip recomputation
  const change = latestChange.current;
  const isPositionOnly = change?.positions === true &&
    !change.atoms && !change.bonds && !change.visibility &&
    !change.arrows && !change.annotations && !change.groups &&
    !change.hydrogenBonds;

  // Clear the change after reading it (one-shot)
  latestChange.current = undefined;

  const generation = useSVGGeneration(workspace, svgWidth, svgHeight, alignment);

  // Use cached result if position-only change and we have a previous result
  const effectiveGeneration = (isPositionOnly && cachedGenerationRef.current)
    ? cachedGenerationRef.current
    : generation;

  // Cache for next time
  if (!isPositionOnly) {
    cachedGenerationRef.current = generation;
  }

  const lockedViewBox = useLockedViewBox(effectiveGeneration.calculatedViewBox);

  const value = useMemo<SVGGenerationContextValue>(() => ({
    ...effectiveGeneration,
    lockedViewBox,
  }), [effectiveGeneration, lockedViewBox]);

  return (
    <SVGGenerationContext.Provider value={value}>
      {children}
    </SVGGenerationContext.Provider>
  );
}

/**
 * Access SVG generation data from the nearest SVGGenerationProvider.
 *
 * @throws Error if used outside SVGGenerationProvider
 */
export function useSVGGenerationData(): SVGGenerationContextValue {
  const context = useContext(SVGGenerationContext);
  if (!context) {
    throw new Error(
      'useSVGGenerationData must be used within a SVGGenerationProvider. ' +
      'Add <SVGGenerationProvider> above your <ComposableSVG> in the component tree.'
    );
  }
  return context;
}
